import { useEffect, useState } from "react"
import { userSelectors } from "store/apis/userAPI"
import { useAppDispatch, useAppSelector } from "store/index"
import { getHappyHour, happyHourSelectors } from "store/apis/happyHourAPI"
import { isDateExpired } from "helpers/isDateExpired"
import { ServerDate } from "helpers/server-date.class.ts"
import { calculateRemainingTime } from "helpers/calculateRemainingTime"

const useHappyHour = (): IUseHappyHour => {
  const dispatch = useAppDispatch()

  const { data: happyHourInfo } = useAppSelector(
    happyHourSelectors.selectHappyHour
  )
  const { data: user } = useAppSelector(userSelectors.selectCurrentUser)

  const [isHappyHour, setIsHappyHour] = useState<boolean>(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)

  const refreshHappyHourInfo = () => {
    dispatch(getHappyHour.initiate(undefined, { forceRefetch: true }))
  }

  useEffect(() => {
    if (!user?.telegram) return

    if (
      !happyHourInfo ||
      !happyHourInfo.isHappyHourNow ||
      !happyHourInfo.startedAt
    )
      return
    const remainingTime = calculateRemainingTime(happyHourInfo.startedAt)

    const timeLeft = 60 * 60 * 24 * 1000 - remainingTime

    if (timeLeft > 0) {
      const timerId = setTimeout(() => {
        refreshHappyHourInfo()
      }, timeLeft)

      return () => clearTimeout(timerId)
    }
  }, [happyHourInfo])

  useEffect(() => {
    const checkIsHappyHour = () => {
      if (happyHourInfo?.startedAt && happyHourInfo?.finishedAt) {
        return (
          isDateExpired(happyHourInfo.startedAt) &&
          !isDateExpired(happyHourInfo.finishedAt)
        )
      }
      return false
    }

    setIsHappyHour(checkIsHappyHour())
  }, [happyHourInfo])

  useEffect(() => {
    if (happyHourInfo?.startedAt || happyHourInfo?.finishedAt) {
      const now = ServerDate.now()

      const startAtTime = happyHourInfo.startedAt
        ? new ServerDate(happyHourInfo.startedAt).getTime() - now
        : null
      const finishAtTime = happyHourInfo.finishedAt
        ? new ServerDate(happyHourInfo.finishedAt).getTime() - now
        : null

      const timers: NodeJS.Timeout[] = []

      if (startAtTime && startAtTime > 0) {
        timers.push(
          setTimeout(() => {
            refreshHappyHourInfo()
          }, startAtTime)
        )
      }

      if (finishAtTime && finishAtTime > 0) {
        timers.push(
          setTimeout(() => {
            setIsHappyHour(false)
          }, finishAtTime)
        )
      }

      return () => timers.forEach(clearTimeout)
    }
  }, [happyHourInfo])

  useEffect(() => {
    if (isHappyHour && happyHourInfo?.finishedAt) {
      const finishTime = new ServerDate(happyHourInfo.finishedAt).getTime()

      const intervalId = setInterval(() => {
        const now = ServerDate.now()
        const timeLeft = finishTime - now

        if (timeLeft <= 0) {
          setTimeRemaining(0)
          refreshHappyHourInfo()
          clearInterval(intervalId)
        } else {
          setTimeRemaining(timeLeft)
        }
      }, 1000)

      return () => clearInterval(intervalId)
    } else {
      setTimeRemaining(null)
    }
  }, [isHappyHour, happyHourInfo])

  useEffect(() => {
    refreshHappyHourInfo()
  }, [])

  return {
    happyHourInfo,
    isHappyHour,
    refreshHappyHourInfo,
    timeRemaining
  }
}

export default useHappyHour
